import React, { Fragment, useEffect, useRef, useState } from "react";
import "./editor.scss";
import Switch from "react-switch";
import useKeyPress from "./useKeypress";
import Select, { components } from "react-select";
import Editor from "@monaco-editor/react";
import { ThreeDots } from "../../Loader/loader";
import { useDispatch, useSelector } from "react-redux";
import {
  compileCode,
  submitCode,
  getNotebookUrl,
  submitNotebook,
} from "../../Redux/ActionCreators/candidateTestAction";
import { returnErrors } from "../../Redux/ActionCreators/errorAction";
import { useTranslation } from "react-i18next";

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <img src="/images/select-arrow.svg" alt="" width="15px" />
    </components.DropdownIndicator>
  );
};

const Main = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const loadStart = useRef(0);
  const notebookConnected = useRef(false);
  const notebookUrlFromReduxRef = useRef("");

  const [code, setCode] = useState("");
  const [value, setValue] = useState(code || "");
  const [processing, setProcessing] = useState(null);
  const [compile, setCompile] = useState(false);
  const [check, setCheck] = useState(false);
  const [theme, setTheme] = useState("vs-light");
  const [active, setActive] = useState("");
  const [submit, setSubmit] = useState(false);
  const [testCases, setTestCases] = useState(null);
  const [outputStatus, setOutputStatus] = useState(null);
  const [output, setOutput] = useState(null);
  const [parsedOutput, setParsedOutput] = useState([]);
  const [compileTestCases, setCompileTestCases] = useState([]);
  const [notebookLoading, setNotebookLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [canLoadIframe, setCanLoadIframe] = useState(false);
  const [notebookUrlLocal, setNotebookUrlLocal] = useState("");
  const [leftWidth, setLeftWidth] = useState("50%");
  const [connectTimer, setConnectTimer] = useState(120);

  const retryLimit = 3;

  const [language, setLanguage] = useState(
    props?.question?.languages?.map(
      (el) =>
        [{ ...el, label: el.displayLanguage, value: el.codingLanguage }][0]
    )[0]
  );

  const isDatabase = props.question?.questionTypeID === 12;
  const isCoding = props.question?.questionTypeID === 7;

  const enterPress = useKeyPress("Enter");
  const ctrlPress = useKeyPress("Control");

  useEffect(() => {
    setCode(props.question?.defaultCode);
    onChangeCode("code", props.question?.defaultCode);
  }, []);

  useEffect(() => {
    setLanguage(
      props.question?.languages?.map(
        (el) =>
          [{ ...el, label: el.displayLanguage, value: el.codingLanguage }][0]
      )[0]
    );
    setCode(props.question?.defaultCode);
    onChangeCode("code", props.question?.defaultCode);
  }, [props.question?.defaultCode]);

  useEffect(() => {
    if (compile) {
      setTimeout(() => {
        const elem = document.querySelector(".complie-window");
        if (elem) {
          elem.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 200);
    }
  }, [compile, active]);

  const handleChangeMode = async (e) => {
    await setCheck(e);
    await setTheme(e ? "vs-dark" : "vs-light");
  };

  const onSelectChange = (sl) => {
    setLanguage(sl);
    setValue(sl.defaultCode);
    onChangeCode("code", sl.defaultCode);
  };

  const handleEditorChange = (value) => {
    setValue(value);
    onChangeCode("code", value);
  };

  useEffect(() => {
    if (enterPress && ctrlPress) {
      handleCompile();
    }
  }, [ctrlPress, enterPress]);

  const onChangeCode = (action, data) => {
    switch (action) {
      case "code": {
        setCode(data);
        setValue(data);
        break;
      }
      default: {
        console.warn(t("case not handled!"), action, data);
      }
    }
  };

  const handleCompile = async (e) => {
    e.preventDefault();
    if (props.question?.questionTypeID === 13) return;

    setProcessing(true);
    setCompile(true);
    setActive("run");

    const formData = {
      token: props.inviteToken,
      candidateTestId: props.question?.candidateTestId,
      candidateTestQuestionId: props.question.candidateQuestionId,
      language: language?.value,
      sourceCode: code,
      inputs: props.question.inputs || [],
      output: "",
    };
    if (!language?.value) {
      returnErrors(dispatch, t("Please select language"), 500);
    } else {
      const res = await dispatch(compileCode(formData, props.token));
      if (res?.type === "RUN_CODE") {
        setProcessing(false);

        const oc = res?.payload?.ocResponse;

        setOutputStatus(oc);
        setOutput(oc?.stdout);
        setParsedOutput(oc?.parsedOutput || []);
        setCompileTestCases(res?.payload?.testCases || []);
        onChangeCode("code", code);
      }
    }
    setProcessing(false);
  };

  const handleSubmitNotebook = async () => {
    setProcessing(true);

    const body = {
      token: props.inviteToken,
      candidateTestId: props.question?.candidateTestId,
      candidateTestQuestionId: props.question?.candidateQuestionId,
    };

    dispatch(submitNotebook(body, props.token));
    setConnectTimer(120)
    setProcessing(false);
    await goNext();
  };

  const codeSubmit = async () => {
    if (props.question?.questionTypeID === 13) {
      return handleSubmitNotebook();
    }
    await setProcessing(true);
    if (!language?.value) {
      returnErrors(dispatch, t("Please select language"), 500);
    } else {
      let obj = {
        token: props.inviteToken,
        candidateTestId: props.question?.candidateTestId,
        candidateTestQuestionId: props.question.candidateQuestionId,
        sourceCode: code,
        language: language?.value,
        inputs: props.question.inputs || [],
        output: "",
      };
      const res = await dispatch(submitCode(obj, props.token));
      if (res?.type === "SUBMIT_CODE") {
        setCode("");
        onChangeCode("code", "");
        await goNext();
        setLanguage(
          props.question?.languages?.map(
            (el) =>
              [
                { ...el, label: el.displayLanguage, value: el.codingLanguage },
              ][0]
          )[0]
        );
        setCompile(false);
      }
    }
    await setProcessing(false);
  };

  const goNext = () => {
    props.getNext();
  };

  useEffect(() => {
    if (props?.question?.questionTypeID !== 13) return;

    setNotebookLoading(true);
    setLoadingFailed(false);
    setRetryCount(0);

    loadNotebookUrl();
  }, [props?.question?.candidateQuestionId]);

    useEffect(() => {
        if (!notebookLoading) return;

        let interval = setInterval(() => {
            setConnectTimer((t) => (t > 0 ? t - 1 : 0));
        }, 1000);

        return () => clearInterval(interval);
    }, [notebookLoading]);

  const loadNotebookUrl = () => {
    dispatch(
      getNotebookUrl(
        props.inviteToken,
        props.question.candidateTestId,
        props.question.candidateQuestionId,
        props.token
      )
    ).then((res) => {
      const freshUrl = res?.payload;

      if (!freshUrl) {
        setLoadingFailed(true);
        setNotebookLoading(false);
        if (props.startTimer && props.question?.questionTypeID === 13) {
          props.startTimer();
        }
        return;
      }
      console.log("Fresh notebook URL:", freshUrl);
      setNotebookUrlLocal(freshUrl);
      attemptNotebookLoad(freshUrl);
    });
  };

  const attemptNotebookLoad = async (url) => {
    if (!url) return;
    if (notebookConnected.current) return;

    setNotebookLoading(true);

    const ok = await verifyNotebookUrl(url);

    if (ok) {
      console.log(
        "Notebook backend ready. Waiting 10 seconds before loading iframe…"
      );

      notebookConnected.current = true;

      // Delay iframe mount by 10 seconds
      setTimeout(() => {
        setCanLoadIframe(true);
        setNotebookLoading(false);
      }, 15000);

      return;
    }

    // Retry logic
    if (retryCount < retryLimit - 1) {
      setRetryCount((prev) => prev + 1);

      setTimeout(() => {
        attemptNotebookLoad(url);
      }, 15000);
    } else {
      setLoadingFailed(true);
      setNotebookLoading(false);
      if (props.startTimer && props.question?.questionTypeID === 13) {
        props.startTimer();
      }
    }
  };

  const handleIframeLoad = () => {
    if (!notebookConnected.current) {
      console.log("Notebook connected for the first time.");
      notebookConnected.current = true;
    }

    // After this moment: NEVER update any notebook state again.
    setNotebookLoading(false);
    setLoadingFailed(false);

    if (props.startTimer && props.question?.questionTypeID === 13) {
      props.startTimer();
    }

    if (props.uploadImage && props.question?.questionTypeID === 13) {
      props.uploadImage();
    }
  };

  const verifyNotebookUrl = async (url) => {
    try {
      const res = await fetch(url, { method: "GET" });
      if (res.ok) return true;

      return false;
    } catch {
      return false;
    }
  };

    const startDrag = (e) => {
        e.preventDefault();

        // Store container rect for accurate calculations
        const container = document.getElementById("resizeContainer");
        if (!container) return;

        const rect = container.getBoundingClientRect();

        const onMouseMove = (event) => {
            const x = event.clientX - rect.left;

            let newWidth = (x / rect.width) * 100;

            if (newWidth >= 20 && newWidth <= 80) {
                setLeftWidth(newWidth + "%");
            }
        };

        const stopDrag = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", stopDrag);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", stopDrag);
    };


  return (
    <div className='coding-container' style={props.question?.questionTypeID === 13 ? { width: "100%" } : {width:'90vw'}}>
      {/* <div className="d-flex flex-wrap" style={{ border: "1px solid #D7DADB" }}> */}
      <div className="d-flex" style={{ border: "1px solid #D7DADB" }}>
          {props.question?.questionTypeID === 13 ? (
            <div
              id="resizeContainer"
              style={{
                display: "flex",
                width: "100%",
                height: "73vh",
                position: "relative",
              }}
            >
              <div
                id="leftPane"
                style={{
                  width: leftWidth,
                  minWidth: "20%",
                  flexShrink: 0,
                  padding: 15,
                  borderRight: "2px solid #D7DADB",
                  overflowY: "auto",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <h5 style={{ marginBottom: 0, fontSize: 18 }}>
                      {props.question?.currentQuestion}/
                      {props.question?.totalQuestion}
                    </h5>
                    <p style={{ marginLeft: 8, fontSize: 18, color: "#555" }}>
                      Data Science
                    </p>
                  </div>
                </div>
                <h2>{props.question?.question}</h2>
                <p
                  className="mt-3"
                  dangerouslySetInnerHTML={{
                    __html: props.question?.description,
                  }}
                />
              </div>
              <div
                onMouseDown={startDrag}
                style={{
                    width: "6px",
                    background: "#ccc",
                    cursor: "col-resize",
                    height: "100%",
                    flexShrink: 0
                }}
            />
              <div
                id="rightPane"
                style={{
                  flex: "1 1 auto",
                  minWidth: "20%",
                  overflow: "hidden",
                  padding: 0,
                }}
              >
                {props.question?.questionTypeID === 13 ? (
                  <Fragment>
                    <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                    <div className="lang-dd"></div>

                    <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                    <div className="mt-2" style={{ height: "64vh" }}>
                      {notebookLoading && (
                        <div
                          className="loading-overlay"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(255,255,255)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 20,
                          }}
                        >
                            <h3 style={{ color: "#000", marginBottom: 15 }}>
                                Connecting to Jupyter Notebook...
                            </h3>

                            <p style={{ fontSize: 18, marginBottom: 20 }}>
                                Time remaining: {connectTimer}s
                            </p>
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">
                              Connecting to notebook...
                            </span>
                          </div>
                        </div>
                      )}

                      {loadingFailed && (
                        <div
                          style={{
                            padding: 20,
                            color: "red",
                            textAlign: "center",
                          }}
                        >
                          Failed to load notebook after 3 attempts.
                          <br />
                          Please check your internet and try again.
                        </div>
                      )}

                      {notebookLoading && (
                        <div style={{ padding: 10, textAlign: "center" }}>
                          Retrying to connect to notebook
                          <br />
                          Attempt {retryCount + 1} of 3
                        </div>
                      )}

                      {canLoadIframe && notebookUrlLocal && (
                        <iframe
                          src={notebookUrlLocal}
                          title="Data Science Notebook"
                          width="100%"
                          height="100%"
                          style={{ border: "none" }}
                          onLoad={handleIframeLoad}
                        />
                      )}
                    </div>
                    <div className="run-code-view">
                      <div className="run-header">
                        <button
                          className="submit-btn"
                          disabled={processing}
                          onClick={handleSubmitNotebook}
                          style={{
                            backgroundColor: processing ? "#D3D3D3" : "#f75891",
                            cursor: processing ? "not-allowed" : "pointer",
                          }}
                        >
                          {processing ? "Submitting..." : "SUBMIT NOTEBOOK"}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                ) : null}
              </div>
            </div>
          ) : (
            <Fragment>
              <div
                className="col-md-5"
                style={{
                  padding: 15,
                  borderRight: "2px solid #D7DADB",
                  maxHeight: "73vh",
                  overflowY: "auto",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <h5 style={{ marginBottom: 0, fontSize: 18 }}>
                      {props.question?.currentQuestion}/
                      {props.question?.totalQuestion}
                    </h5>
                    <p style={{ marginLeft: 8, fontSize: 18, color: "#555" }}>
                      {props.question?.questionTypeID === 13
                        ? "Data Science"
                        : props.question?.questionTypeID === 12
                        ? "Database"
                        : "Coding"}
                    </p>
                  </div>
                  {props.question?.testName && (
                    <div>
                      <strong style={{ fontSize: 18, color: "#333" }}>
                        {props.question?.testName}
                      </strong>
                    </div>
                  )}
                </div>
                <h2>{props.question?.question}</h2>
                <p
                  className="mt-3"
                  dangerouslySetInnerHTML={{
                    __html: props.question?.description,
                  }}
                />
              </div>
              <div className="col-md-7">
                {!submit ?
                <Fragment>
                  <div className="lang-dd">
                    <div class="d-flex  align-items-center">
                      <Switch
                        onChange={(e) => {
                          handleChangeMode(e);
                        }}
                        width={30}
                        height={16}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        onColor="#02c573"
                        checked={check}
                      />
                      &nbsp;&nbsp;
                      <label className="col-form-label">{t("Dark Mode")}</label>
                      &nbsp;&nbsp;
                    </div>
                    <div
                      class="d-flex  align-items-center"
                      style={{ width: "40%" }}
                    >
                      <label className="col-form-label">{t("Language")}</label>
                      &nbsp;&nbsp;
                      <div style={{ width: "70%" }}>
                        <Select
                          components={{ DropdownIndicator }}
                          placeholder={`Filter By Category`}
                          options={props.question?.languages?.map(
                            (el) =>
                              [
                                {
                                  ...el,
                                  label: el.displayLanguage,
                                  value: el.codingLanguage,
                                },
                              ][0]
                          )}
                          // styles={customStyles}
                          value={language}
                          onChange={(selectedOption) =>
                            onSelectChange(selectedOption)
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <img
                        src="/images/reset.svg"
                        alt=""
                        className="cursor-pointer"
                        onClick={() => onChangeCode("code", "")}
                      />
                    </div>
                  </div>
                  <div style={{ borderBottom: "2px solid #D7DADB" }}></div>
                  <div className="mt-2">
                    <Editor
                      height="60vh"
                      width={`100%`}
                      language={language}
                      value={value}
                      theme={theme}
                      defaultValue="// some comment"
                      onChange={handleEditorChange}
                      options={{
                        autoIndent: "full",
                        contextmenu: true,
                        fontFamily: "monospace",
                        lineHeight: 24,
                        hideCursorInOverviewRuler: true,
                        inlineSuggest: true,
                        fontSize: "16px",
                        formatOnType: true,
                        autoClosingBrackets: true,
                        minimap: { enabled: false },
                        scrollbar: {
                          horizontalSliderSize: 4,
                          verticalSliderSize: 18,
                        },
                        selectOnLineNumbers: true,
                        roundedSelection: false,
                        readOnly: false,
                        cursorStyle: "line",
                        automaticLayout: true,
                      }}
                    />

                    <div className="run-code-view">
                      <div className="run-header">
                        <button
                          className="cursor-pointer submit-btn"
                          onClick={(e) => handleCompile(e)}
                        >
                          {t("COMPILE AND RUN CODE")}
                        </button>
                        <button
                          className="submit-btn"
                          disabled={processing}
                          onClick={() => codeSubmit()}
                          style={{
                            backgroundColor: processing ? "#D3D3D3" : "#f75891",
                            cursor: processing ? "not-allowed" : "pointer",
                          }}
                        >
                          {t("SUBMIT CODE")}
                        </button>
                      </div>
                      {compile ? (
                        <div
                          className="complie-window"
                          style={{ minHeight: "200px" }}
                        >
                          {active === "run" ? (
                            <Fragment>
                              <div>
                                <h5>
                                  {processing ? (
                                    <img src="/images/check-grey.svg" alt="" />
                                  ) : (
                                    <img src="/images/check-green.svg" alt="" />
                                  )}
                                  &nbsp;&nbsp;{t("Compiling")}
                                </h5>
                                {outputStatus?.executionStatus === 1 ? (
                                  <div>
                                    {isDatabase &&
                                      parsedOutput &&
                                      parsedOutput.length > 0 && (
                                        <table
                                          className="table table-bordered mt-3"
                                          style={{
                                            background: "#E6FFE6",
                                            borderColor: "green",
                                          }}
                                        >
                                          <thead>
                                            <tr>
                                              {Object.keys(parsedOutput[0]).map(
                                                (col) => (
                                                  <th key={col}>
                                                    {col.toUpperCase()}
                                                  </th>
                                                )
                                              )}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {parsedOutput.map((row, index) => (
                                              <tr key={index}>
                                                {Object.keys(row).map((col) => (
                                                  <td key={col}>{row[col]}</td>
                                                ))}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    {/* CODING TEST → show testCases only */}
                                    {isCoding &&
                                      compileTestCases &&
                                      compileTestCases.length > 0 && (
                                        <div className="mt-3">
                                          <h4 style={{ color: "green" }}>
                                            Test Cases
                                          </h4>

                                          {compileTestCases.map((tc) => (
                                            <div
                                              key={tc.testCaseId}
                                              style={{
                                                border: "1px solid #0A7A0A",
                                                padding: "10px",
                                                borderRadius: "6px",
                                                marginBottom: "12px",
                                                background: "#E3F9E5",
                                              }}
                                            >
                                              <h5
                                                style={{
                                                  color: "#0A7A0A",
                                                  marginBottom: "8px",
                                                }}
                                              >
                                                Test Case #{tc.testCaseId}
                                              </h5>

                                              {/* hidden test case */}
                                              {tc.hidden ? (
                                                <p
                                                  style={{
                                                    color: "green",
                                                    margin: 0,
                                                  }}
                                                >
                                                  ✓ Test case passed
                                                </p>
                                              ) : (
                                                <table
                                                  className="table table-bordered"
                                                  style={{
                                                    background: "#E3F9E5",
                                                    color: "#0A7A0A",
                                                    borderColor: "#0A7A0A",
                                                  }}
                                                >
                                                  <thead>
                                                    <tr>
                                                      <th>Inputs</th>
                                                      <th>Expected</th>
                                                      <th>Actual</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    <tr>
                                                      <td>
                                                        {tc.inputs &&
                                                        Object.keys(tc.inputs)
                                                          .length > 0
                                                          ? Object.entries(
                                                              tc.inputs
                                                            ).map(([k, v]) => (
                                                              <div key={k}>
                                                                {k}: {v}
                                                              </div>
                                                            ))
                                                          : "-"}
                                                      </td>
                                                      <td>
                                                        {tc.expectedOutput ??
                                                          "-"}
                                                      </td>
                                                      <td>
                                                        {tc.actualOutput ?? "-"}
                                                      </td>
                                                    </tr>
                                                  </tbody>
                                                </table>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                ) : outputStatus?.executionStatus === 2 ||
                                  outputStatus?.executionStatus === 3 ? (
                                  <p style={{ color: "red" }}>
                                    {outputStatus?.exception}
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: outputStatus?.stderr,
                                      }}
                                    />
                                  </p>
                                ) : null}
                              </div>
                              {processing ? (
                                <div className="ms-3">
                                  <ThreeDots color="#009199" />
                                </div>
                              ) : null}
                            </Fragment>
                          ) : (
                            active === "testcase" && (
                              <div className="d-flex flex-column">
                                <h2>Test Cases</h2>
                                {Array.isArray(testCases) &&
                                testCases.length > 0 ? (
                                  testCases.map((item) => (
                                    <div key={item.testCaseId}>
                                      <h3
                                        className="d-flex justify-content-between pt-4"
                                        style={{ color: "grey" }}
                                      >
                                        {t(`Test Case #${item.testCaseId}`)}
                                        <span
                                          style={{
                                            color:
                                              item.executionStatus === 1
                                                ? "green"
                                                : "red",
                                            paddingLeft: "10px",
                                          }}
                                        >
                                          {item.executionStatus === 1
                                            ? "Success"
                                            : "Failed"}
                                        </span>
                                      </h3>
                                    </div>
                                  ))
                                ) : (
                                  <p
                                    style={{
                                      color: "grey",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {processing
                                      ? t("Verifying test cases...")
                                      : t("No test cases available")}
                                  </p>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Fragment>
                :
                <div className="text-center" style={{ padding: 150 }}>
                  <img src="/images/green-check.svg" alt="" />
                  <h6>
                    {t(
                      "Your code was submitted successfully. You are now being redirected to the next question"
                    )}
                  </h6>
                  <button
                    className="output-btn"
                    onClick={() =>
                      !props.isNextTest && !props.isTestCompleted
                        ? goNext()
                        : props.isTestCompleted
                        ? props.setActive("feedback")
                        : props.isNextTest
                        ? props.getSummary()
                        : null
                    }
                  >
                    {t("View Next Question")}
                  </button>
                </div>
                }
              </div>
            </Fragment>
          )}
      </div>
    </div>
  );
};

export default Main;